import { Fragment, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../../i18n/language-provider";
import { LanguageToggle } from "../language-toggle";
import { ThemeToggle } from "../theme-toggle";
import { OptivoWordmark } from "../brand/OptivoWordmark";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { cn } from "../ui/utils";
import { COMPANY, formattedAddress } from "./companyDetails";
import {
    articleNumber,
    legalDocument,
    legalDocumentLinks,
    type LegalDocumentKey,
    type LegalSection,
} from "./legalContent";
import { legalOpenPoints, splitOpenPoints } from "./vop/openPoints";

/**
 * The public shell for the legal documents — terms, contact details and the
 * privacy notice.
 *
 * Rendered outside the authenticated shell and before any session probe: the
 * payment gateway reads these pages logged out, so they must not depend on
 * `/api/me` resolving, or on a session at all.
 */

interface LegalPageProps {
    documentKey: LegalDocumentKey;
    /** Client-side navigation, so cross-links stay inside the SPA. */
    onNavigate: (path: string) => void;
    /**
     * The header's way back. A signed-in operator who opened the privacy
     * notice from the cookie banner came from a page of the app, and "back"
     * means that page rather than the front door; without one, it is home.
     */
    onBack?: () => void;
}

/**
 * A text of a document, with any open point in it marked: the general terms
 * were published with values the company has still to set (27 Oct 2026),
 * and each shows where it belongs in its sentence.
 */
function LegalText({ text }: { text: string }) {
    const parts = splitOpenPoints(text);
    if (parts.every((part) => !part.open)) return <>{text}</>;
    return (
        <>
            {parts.map((part, index) =>
                part.open ? (
                    <mark key={index} className="rounded-sm bg-warning-surface px-1 text-foreground">
                        {part.text}
                    </mark>
                ) : (
                    <Fragment key={index}>{part.text}</Fragment>
                ),
            )}
        </>
    );
}

/** The column a clause's number stands in, and the indent of what hangs under its text. */
const CLAUSE_NUMBER = "font-mono text-xs leading-[1.625rem] text-muted-foreground tabular-nums";

/**
 * The numbered clauses of a contract. The numbers are printed text and not a
 * list's own counter, because a clause is referred to by its number from
 * other clauses and from the annexes ("čl. 11.4"), and a reader has to be
 * able to find, select and quote it.
 *
 * A clause's points stand under its text from `sm` up and under its number
 * below that: on a phone two columns of numbers would leave a definition
 * half the screen. The page is the whole window (it has no rail), so the
 * viewport's width is the page's own.
 */
function Clauses({ section }: { section: LegalSection }) {
    const article = articleNumber(section);
    return (
        <ol className="mt-3 list-none space-y-3 text-sm leading-relaxed">
            {(section.clauses ?? []).map((clause, index) => {
                const number = article === null ? String(index + 1) : `${article}.${index + 1}`;
                return (
                    <li key={number} data-clause={number} className={cn(clause.emphasized && "font-semibold")}>
                        <div className="grid grid-cols-[2.75rem_minmax(0,1fr)] gap-x-2">
                            <span className={CLAUSE_NUMBER}>{number}</span>
                            <p>
                                <LegalText text={clause.text} />
                            </p>
                        </div>
                        {clause.items && (
                            <ol className="mt-1.5 list-none space-y-1.5 sm:ml-[3.25rem]">
                                {clause.items.map((item, itemIndex) => (
                                    <li key={itemIndex} className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-x-2">
                                        <span className={CLAUSE_NUMBER}>{`${number}.${itemIndex + 1}`}</span>
                                        {typeof item === "string" ? (
                                            <span>
                                                <LegalText text={item} />
                                            </span>
                                        ) : (
                                            <span>
                                                <strong className="font-semibold">{item.term}</strong>{" "}
                                                <LegalText text={item.text} />
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        )}
                        {clause.closing && (
                            <p className="mt-1.5 ml-[3.25rem]">
                                <LegalText text={clause.closing} />
                            </p>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}

function Section({ section }: { section: LegalSection }) {
    return (
        <section id={section.id} className="mt-8 scroll-mt-20 first:mt-0">
            <h2 className="text-lg font-semibold tracking-tight">{section.heading}</h2>
            {/* One column below sm, so a flat gap puts the same 4px between a
                term and its value as between one pair and the next, and the
                identification reads as twelve unpaired lines. The row gap
                opens to 16px and the value pulls itself back up by 12px, which
                leaves 4px inside a pair and 16px between them. The spacing has
                to come from the dl: the `contents` wrapper makes every <dt>
                the first child of its own div, so a `first:` variant on the
                term would zero all of them. */}
            {section.definitions && (
                <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[minmax(0,14rem)_1fr] sm:gap-y-1">
                    {section.definitions.map((entry) => (
                        <div key={entry.term} className="contents">
                            <dt className="text-sm text-muted-foreground">{entry.term}</dt>
                            <dd className="-mt-3 text-sm sm:mt-0">{entry.value}</dd>
                        </div>
                    ))}
                </dl>
            )}
            {section.paragraphs?.map((paragraph) => (
                <p key={paragraph} className="mt-3 text-sm leading-relaxed">
                    <LegalText text={paragraph} />
                </p>
            ))}
            {section.bullets && (
                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm leading-relaxed">
                    {section.bullets.map((bullet) => (
                        <li key={bullet}>
                            <LegalText text={bullet} />
                        </li>
                    ))}
                </ul>
            )}
            {section.clauses && <Clauses section={section} />}
            {section.notes?.map((note) => (
                <p key={note} className="mt-3 text-sm leading-relaxed">
                    <LegalText text={note} />
                </p>
            ))}
        </section>
    );
}

export function LegalPage({ documentKey, onNavigate, onBack }: LegalPageProps) {
    const { language, t } = useLanguage();
    const doc = legalDocument(documentKey, language);

    const effectivePrefix = language === "cs" ? "Účinné od" : "Effective from";

    // An address naming an article (`/terms#push`, linked from the consent
    // screen) is scrolled to once the page is drawn: the browser looked for
    // the anchor before the page existed.
    useEffect(() => {
        const id = decodeURIComponent(window.location.hash.slice(1));
        if (id) document.getElementById(id)?.scrollIntoView?.();
    }, [documentKey]);

    // The values still to be set, counted once at the head of the document.
    const openPoints = legalOpenPoints(doc);

    // Read from the one list rather than restated here: these labels were
    // hardcoded in both languages beside the documents they name, drifted
    // from the titles, and left out the subscription and recurring-payment
    // terms — the two an operator most wants to reach from the others.
    const otherDocuments = legalDocumentLinks(language).filter((link) => link.key !== documentKey);

    const handleCrossLink = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
        // Keep real hrefs for crawlers and for the gateway's review, but stay
        // in the SPA when a human clicks.
        event.preventDefault();
        onNavigate(path);
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
                <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-6">
                    <Button
                        variant="ghost"
                        className="-ml-3 gap-2 text-base font-semibold"
                        onClick={() => (onBack ? onBack() : onNavigate("/"))}
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <OptivoWordmark className="h-6 text-primary dark:text-foreground" />
                    </Button>
                    <div className="flex items-center gap-1">
                        <LanguageToggle />
                        <ThemeToggle />
                    </div>
                </div>
            </header>

            <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-10">
                <h1 className="text-3xl font-semibold tracking-tight">{doc.title}</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    {effectivePrefix} <LegalText text={doc.effectiveDate} />
                </p>
                {openPoints.length > 0 && (
                    <p
                        role="note"
                        className="mt-4 rounded-md border border-warning bg-warning-surface px-4 py-3 text-sm leading-relaxed"
                    >
                        {t.legalOpenPoints.note.replace("{count}", String(openPoints.length))}
                    </p>
                )}
                {doc.intro?.map((paragraph) => (
                    <p key={paragraph} className="mt-4 text-sm leading-relaxed text-muted-foreground">
                        <LegalText text={paragraph} />
                    </p>
                ))}

                <Separator className="my-8" />

                {doc.sections.map((section) => (
                    <Section key={section.id} section={section} />
                ))}
            </main>

            <Separator />

            <footer className="mx-auto w-full max-w-3xl px-6 py-8 text-sm text-muted-foreground">
                {/* The gateway checks the identification on every public
                    page, not only on /contact — name, company ID, the full
                    seat, and a reachable e-mail and phone. The website closes
                    the line as the review of the terms had it (29 Sep 2026). */}
                <p>
                    {COMPANY.legalName}, IČO {COMPANY.ico}, {formattedAddress(language)}
                </p>
                <p className="mt-1">
                    {COMPANY.email} · {COMPANY.phone} ·{" "}
                    <a
                        href={COMPANY.website}
                        className="hover:text-foreground transition-colors underline underline-offset-2"
                    >
                        {COMPANY.website.replace(/^https?:\/\//, "")}
                    </a>
                </p>
                {/* Same 19px-target problem as the welcome footer's nav: these
                    are how the gateway reviewer moves between the documents on
                    a phone. `min-h-10` grows the hit box without moving the
                    baseline, and `sm:` puts desktop back where it is today. */}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 sm:gap-y-4 [&>*]:inline-flex [&>*]:items-center [&>*]:min-h-10 sm:[&>*]:min-h-0">
                    {otherDocuments.map((link) => (
                        <a
                            key={link.key}
                            href={link.path}
                            onClick={(event) => handleCrossLink(event, link.path)}
                            className="hover:text-foreground transition-colors underline underline-offset-2"
                        >
                            {link.title}
                        </a>
                    ))}
                </div>
            </footer>
        </div>
    );
}
